import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import {
  cellChordBeats,
  cellSlots,
  DEFAULT_BARS_PER_LINE,
  isMelodyCell,
  narrowsHolds,
  REST_MARK,
  wrapLines,
} from '../../utils/chordBars';
import { INVERSION_ORDINALS, splitInversion } from '../../utils/chordNotes';
import { chordEm, REST_EM } from '../../utils/chordGlyphs';
import RestMark from '../RestMark';

import './ChordBarsChart.scss';

/**
 * A bar-line chart, drawn as a grid instead of as the text it is stored as.
 *
 * The chart lives in a part's lyrics as "| C  |    | G  |    |" and used to render through the
 * generic lyrics path: literal pipes, in a proportional font, with the cells padded to equal
 * character counts because that was the closest thing to alignment available there (see
 * formatBarLines). Here the bars are real boxes, so the columns line up exactly and the padding
 * stops mattering.
 *
 * Everything is sized in `em` so the player's font-size control and the print sheet's 10pt both
 * carry through without a second code path — and it is built from spans, because it renders inside
 * the inline `.music-tag-lyrics` span rather than beside it.
 *
 * @param {string[][]} lines - Grid from getPartGrid: lines of cells, one cell per bar. An empty
 *   cell is a held bar; a cell can hold several chords sharing the bar ("Em C").
 * @param {number} [repeatCount] - Draws repeat signs around the block when it is 2 or more. The
 *   caller suppresses applyRepeatSlashes in that case — slashes exist to mark up text, and on a
 *   grid the indent they carry is meaningless.
 * @param {number} [beatsPerBar] - Widens each chord in a shared bar by the beats it actually holds,
 *   so the chart shows where the changes land. Without it — a caller with no time signature to hand
 *   — they spread evenly, which is all the chart could say before.
 * @param {number[]} [sections] - Which section each line belongs to, where a section is a run of
 *   lines belonging to one instrument (see chordBars). Only ever draws a rule: sections are sequential, so
 *   the grid below is the one continuous chart it was without them, and nothing about sizing, bar
 *   counting or the sounding bar changes. A chart with one section is drawn exactly as before.
 * @param {number} [currentBar] - The bar sounding right now, counted across the whole chart, which
 *   is how useChordGridPlayer reports it. Lights that bar up while the player runs, so the chart
 *   being heard is the chart being read — the same thing the builder's grid does with its cells.
 * @param {number} [currentChord] - The chord ringing right now, as an index into the chart's
 *   strikes (getChordStrikes), which is the nth chord written in it. Fills a rule under that one
 *   chord over `currentChordSeconds`, so a held chord shows how far through the hold it is rather
 *   than only that it is the one sounding.
 * @param {number} [currentChordSeconds] - How long that chord rings for. The rule under it is a
 *   CSS animation of exactly this length, so nothing re-renders between one chord and the next.
 * @param {boolean} [countInEntry] - The chart comes in during the count-in: the rest its first bar
 *   opens on is the count, and is drawn as its numbers rather than as a rest.
 * @param {function} [chordFamily] - Handed a chord, returns the Chord Library family to colour it
 *   by (see getChordFamily), or null to leave it the accent. View passes it for out-of-key chords.
 * @param {function} [spellChord] - Handed a chord, returns it as it should be shown (see
 *   partKey's spellChord). Left out, chords are drawn as written, which is what the editor wants.
 * @param {function} [onOpen] - Makes the chart tappable, handed the click. Whoever passes it
 *   decides what a tap means: in View a chord still opens the piano and everything else opens the
 *   player, and none of that policy belongs to a component that only draws the notation.
 */
/*
 * Sizing the chart to the room it has.
 *
 * The player's font-size control goes to 40px, where four bars at a 3em basis want 640px and a
 * phone has about 360px — so past a certain size the line simply runs off the screen. Rather than
 * let it, the chart stops growing at the largest size that still fits: the numbers below say how
 * many multiples of its own font size one line needs, and the stylesheet divides the container's
 * width by that (see the clamp on `.fit`).
 *
 * All of it rests on knowing how wide a chord is — see chordEm.
 */
// A beat mark, at the size it is actually drawn: `/` is 0.58em of glyph, and the stylesheet writes
// it at the chord's own size so the two share a cap height. Marks only appear in an uneven bar, and
// the widest cell sets the width of every bar in the chart, so a bar with its beats written out
// does now pay full width for them — and every bar on its line pays with it.
const MARK_EM = 0.58;
// Everything in a bar that is not the chord. The app is border-box throughout, so a bar's width has
// to swallow its own 0.45em padding either side and the bar line drawn on its edge — leaving the
// chord the remainder. Under-counting this is what pushes a chord over the bar line.
const BAR_PADDING_EM = 1.15;
const MIN_BAR_EM = 3; // the width a bar never goes under, however short its chord
// ...and the width it stops growing past. Bars grow to fill the line, and on a desktop that leaves
// a four-bar chart as four enormous boxes of mostly gap. Roughly where a cell lands in the builder's
// own chart column, so a chart reads on the page at about the size it was written at. In em, so the
// ceiling rises with the player's font-size setting rather than fighting it.
const MAX_BAR_EM = 7;
const REPEAT_EM = 4; // two repeat signs plus the ×N label
// A held bar drawn narrow beside busy ones (see narrowsHolds): its padding, bar line and hold mark
const NARROW_BAR_EM = 2.4;
// ...and the breathing room a busy bar beside them gets past its chords' full width. Room past that
// goes back to the held bars, so one busy bar doesn't spread its chords across the whole line.
const WIDE_BAR_SLACK_EM = 1.5;
// What separates two chords sharing a bar — the `gap` on `.bar` in the stylesheet. A cell's raw
// string length would charge a full character for it, and since the widest cell sets the width of
// every bar in the chart, over-stating it there costs the whole chart rather than one bar.
const CHORD_GAP_EM = 0.3;
// How small a shared bar's chords may be written before the bar widens for them instead. Two changes
// in a bar are engraved smaller than one anyway, so this is notation as much as it is fit — but past
// about here the pair stops being readable and the honest answer is a wider bar.
const MIN_SHARED_SCALE = 0.72;
// A slash chord carries a second symbol for one change, so it is drawn smaller than the triads it
// sits beside — at this scale "E/G#" costs about what "C#m" does, and a bar holding one stops being
// the widest thing in the chart. The builder writes them full size: there a cell is being edited,
// and the bass is what is being typed.
const SLASH_SCALE = 0.8;

/**
 * Whether a bar writes its beats out as marks.
 *
 * Only where a mark *is* a beat. A slash is the one piece of chart notation everybody already reads,
 * and what it says is "one beat" — so on a bar written finer than its own beats, where cellChordBeats
 * scales eight marks onto four clicks, a row of them says something the bar does not mean: "Am / /"
 * on the eighth grid is a chord held for a beat and a half, and three slashes read as three beats.
 * Such a bar is spaced instead, by the widths below — which is how a real chart engraves a change
 * landing off the beat anyway, the symbol sitting where it is played rather than on a click.
 */
const drawsMarks = (cell, beatsPerBar) => {
  const slots = cellSlots(cell);
  const bar = Number(beatsPerBar) > 0 ? Number(beatsPerBar) : null;

  return slots != null && (!bar || slots <= bar);
};

/** The beat marks written into a cell — none at all in a bar that was left to divide evenly. */
// What a bar writes out as symbols: its chords, and its rests as REST_MARK (sized as their glyph)
const cellSymbols = (cell) =>
  cellChordBeats(cell).flatMap(({ chord, rest }) => {
    if (rest) return [REST_MARK];

    return chord === null ? [] : [chord];
  });

const cellMarks = (cell, beatsPerBar) =>
  drawsMarks(cell, beatsPerBar) ? Math.max(0, (cellSlots(cell) ?? 0) - cellSymbols(cell).length) : 0;

/**
 * A chord carrying a bass — "E/G#". cellChords hands back symbols only, so a lone beat mark can't
 * reach this: a `/` written as a beat is slots, and a tie carries no symbol at all.
 */
const isSlashChord = (chord) => chord.includes('/');

/** How wide a chord is drawn here, which is not how wide it is written — see SLASH_SCALE. */
const drawnEm = (chord) => {
  if (chord === REST_MARK) return REST_EM;

  return chordEm(chord) * (isSlashChord(chord) ? SLASH_SCALE : 1);
};

/** What a cell's chords need, glyphs only — the part that shrinks when a bar is written down. */
const cellGlyphEm = (cell, beatsPerBar) =>
  cellSymbols(cell).reduce((total, chord) => total + drawnEm(chord), 0) + cellMarks(cell, beatsPerBar) * MARK_EM;

/**
 * ...and the separation between them, which doesn't: a whole gap between two smaller chords reads
 * fine.
 *
 * Every bar pays it, however it was written. A marked bar used to pay nothing, on the grounds that
 * it is spaced by its beats and `.bar--marked` takes the flex gap off — but the beats space the
 * *boxes*, not the symbols in them, so two changes in a row printed flush against each other
 * ("C#mE/G#"). It pays as padding inside the boxes instead, which separates the symbols without
 * moving where the next beat starts (see `.bar--marked` in the stylesheet).
 */
const cellGapEm = (cell) => Math.max(0, cellSymbols(cell).length - 1) * CHORD_GAP_EM;

/** A bar with more than one change written in it, which is the bar all of this turns on. */
const isShared = (cell) => cellSymbols(cell).length > 1;

/**
 * The numbers the stylesheet sizes the chart from: one bar's width, what a whole line costs, how
 * wide the chart may get, and the pieces `.bar--shared` needs to decide how far to write a shared
 * bar down.
 *
 * Every bar takes the same width — the widest chord's in the whole chart, not its own. That is what
 * puts the bar lines of one row under the bar lines of the row above it; sizing each bar to its own
 * content instead leaves every row's columns somewhere different.
 *
 * Which is why one bar holding two changes used to cost the whole chart: "F#m E" is half again as
 * wide as the "C#m" beside it, so every bar took that width, the line stopped fitting the screen,
 * and the clamp on `.fit` shrank all four columns to serve one of them. So the bars are sized to
 * hold a shared cell written down to MIN_SHARED_SCALE, and a shared bar makes up the difference by
 * writing its chords smaller.
 *
 * How much smaller is deliberately *not* decided here. Whether a bar is short of room is a fact
 * about the screen it landed on, not about what is written in it — a chart that has to squeeze on a
 * phone has room to spare on a desktop, where `.fit` stops widening at MAX_BAR_EM and a bar is
 * 7em across. Baking a scale in from the text alone shrank the pair on both. So these hand over the
 * chords' own width and let the stylesheet's clamp measure it against the room the bar actually
 * got.
 */
const chartMetrics = (lines, repeats, beatsPerBar, barsPerLine, narrowBars) => {
  const cells = lines.flat();
  const shared = cells.filter(isShared);
  const glyphEm = (cell) => cellGlyphEm(cell, beatsPerBar);

  const singleEm = Math.max(0, ...cells.filter((cell) => !isShared(cell)).map(glyphEm));
  const sharedEm = Math.max(0, ...shared.map(glyphEm));
  const sharedGapEm = Math.max(0, ...shared.map(cellGapEm));

  // The narrowest a shared bar may be drawn: its glyphs at the floor, its gaps whole. Bars never go
  // under this, so the stylesheet's clamp always has somewhere to land.
  const widestCell = Math.max(singleEm, sharedEm * MIN_SHARED_SCALE + sharedGapEm);
  const barEm = Math.max(MIN_BAR_EM, widestCell + BAR_PADDING_EM);
  const repeatEm = repeats ? REPEAT_EM : 0;
  const wideBars = barsPerLine - narrowBars;
  const narrowEm = narrowBars * NARROW_BAR_EM;
  const fullEm = Math.max(0, ...cells.map((cell) => glyphEm(cell) + cellGapEm(cell)));
  const wideMaxEm = Math.max(barEm, fullEm + BAR_PADDING_EM + WIDE_BAR_SLACK_EM);

  return {
    '--bar-em': Number(barEm.toFixed(2)),
    '--bars-per-line': barsPerLine,
    '--narrow-bar-em': NARROW_BAR_EM,
    '--repeat-em': repeatEm,
    // What `.bar--shared` measures against the room it got — see the clamp in the stylesheet
    '--shared-em': Number(sharedEm.toFixed(2)),
    '--shared-gap': Number(sharedGapEm.toFixed(2)),
    '--wide-bars': wideBars,
    '--narrow-em': narrowEm,
    '--wide-max-em': Number(wideMaxEm.toFixed(2)),
    '--min-shared': MIN_SHARED_SCALE,
    // Quoted to the stylesheet so the size a slash chord is drawn at and the width the bars were
    // sized for are one number
    '--slash-scale': SLASH_SCALE,
    '--fit-units': Number((barsPerLine * barEm + repeatEm).toFixed(2)),
    // The same, with held bars drawn narrow — the stylesheet switches to it on a phone-width chart
    '--narrow-fit-units': Number((wideBars * barEm + narrowEm + repeatEm).toFixed(2)),
    // A chord wider than the ceiling sets its own: the bar has to hold what is written in it. Narrow
    // bars don't lower it, so the room they give up goes to the wide bars rather than off the line.
    '--fit-max': Number((barsPerLine * Math.max(MAX_BAR_EM, barEm) + repeatEm).toFixed(2)),
    // The bars' own share of it. Repeat signs take less than REPEAT_EM, and without this cap what
    // they leave widens a repeated chart's bars past every other chart's on a wide screen.
    '--bars-max': Number((barsPerLine * Math.max(MAX_BAR_EM, barEm)).toFixed(2)),
  };
};

/** The heavier bar line that opens a section — the break, drawn. */
const sectionClass = (sections, line) =>
  line > 0 && (sections[line] ?? 0) !== (sections[line - 1] ?? 0) ? ' bar-line--section-start' : '';

/** The last line of the chart, or of a section — the lines a short row gets padded out on. */
const closesSection = (lines, sections, line) =>
  line === lines.length - 1 || (sections?.[line + 1] ?? 0) !== (sections?.[line] ?? 0);

function ChordBarsChart({
  lines: storedLines,
  sections: storedSections,
  repeatCount,
  beatsPerBar,
  countInEntry = false,
  currentBar = -1,
  currentChord = -1,
  currentChordSeconds = 0,
  chordFamily,
  spellChord = (chord) => chord,
  onOpen,
}) {
  const { t } = useTranslation();
  const repeats = Number(repeatCount) >= 2 ? Number(repeatCount) : null;
  const beats = Number(beatsPerBar) > 0 ? Number(beatsPerBar) : null;

  if (!storedLines?.length) return null;

  // Wrapped the way the builder opens it. A line with no chord on it — a bar of rest — is stored
  // joined onto the line above (see normalizeGrid), so without this it draws as a fifth bar there.
  const { lines, sections } = wrapLines(storedLines, storedSections);
  const divided = (sections[sections.length - 1] ?? 0) > 0;

  // A line closing the chart or a section short of a full line is drawn out with greyed, empty bars,
  // so the block ends square. Display only — nothing is written back, and they sit after the line's
  // real bars, so the bar and strike counts below are untouched.
  const barsPerLine = Math.max(DEFAULT_BARS_PER_LINE, ...lines.map((cells) => cells.length));
  const fillersFor = (line) => (closesSection(lines, sections, line) ? barsPerLine - lines[line].length : 0);

  // On a one-line chart with a busy bar, held bars and fillers give their width to the rest
  const narrow = narrowsHolds(lines);
  const narrowBars = narrow ? lines[0].filter((cell) => cell === '').length + fillersFor(0) : 0;
  // Which narrow bars take what the busy ones don't need: the held bars, or the fillers if none
  const holdsTakeSpare = lines[0].includes('');
  const barMode = (cell) => {
    if (!narrow) return '';

    if (cell !== '') return ' bar--capped';

    return holdsTakeSpare ? ' bar--narrow bar--spare' : ' bar--narrow';
  };

  // Every bar before each line, so the sounding bar's flat index can be found on the row it is in
  const barsBefore = lines.reduce((counts, cells) => [...counts, counts[counts.length - 1] + cells.length], [0]);

  // Which strike each chord is, counted as it is drawn. The player reports the ringing chord as an
  // index into getChordStrikes, and that list is getChartChords' list — every chord written, in
  // reading order, ties and empty bars carrying none. Counting them here as they are laid out is
  // the same pass, so the chord lit is the chord heard.
  let strikes = 0;

  return (
    <span
      className={`ChordBarsChart${repeats ? ' ChordBarsChart--repeat' : ''}${onOpen ? ' ChordBarsChart--tappable' : ''}`}
      style={chartMetrics(lines, repeats, beats, barsPerLine, narrowBars)}
      onClick={onOpen}
    >
      <span className="fit">
        {repeats && <span className="repeat-sign repeat-sign--open" aria-hidden="true" />}

        <span className="bar-lines">
          {lines.map((cells, line) => (
            // Lines have no identity of their own — the grid is positional, and reordering happens
            // in the builder against its own state, not here
            // eslint-disable-next-line react/no-array-index-key
            <span className={`bar-line${divided ? sectionClass(sections, line) : ''}`} key={line}>
              {cells.map((cell, bar) => (
                <span
                  className={`bar${isShared(cell) ? ' bar--shared' : ''}${cellSlots(cell) ? ' bar--marked' : ''}${isMelodyCell(cell) ? ' bar--melody' : ''}${barMode(cell)}${currentBar === barsBefore[line] + bar ? ' bar--sounding' : ''}`}
                  // eslint-disable-next-line react/no-array-index-key
                  key={bar}
                >
                  {/* A bar played as single notes says so in its corner */}
                  {isMelodyCell(cell) && (
                    <span className="melody-badge" aria-label={t('player.melody')}>
                      ♪
                    </span>
                  )}
                  {cell === '' ? (
                    // A bar with nothing written in it is the chart saying the chord is still
                    // sounding, so it takes the same rule the builder's held cells do
                    <span className="hold-mark" aria-hidden="true" />
                  ) : (
                    // data-chord is what ViewSongPart's tap handler reads to open the piano, so a
                    // chord in a bar behaves like one written over a syllable
                    // ...and a chord sharing the bar is as wide as the beats it holds, so where its
                    // box starts is where it is played
                    cellChordBeats(cell, beats).map(({ chord, beats: held, slots, rest }, index) => {
                      // A mark is a beat or it is not drawn — see drawsMarks. The two part company
                      // exactly when the bar was written finer than its own beats.
                      const marks = chord !== null && slots > 1 && held === slots ? slots - 1 : 0;

                      // A tie draws as the rule an empty bar draws, sized to the beats it holds:
                      // the same "still sounding" a whole held bar says, said for part of one. No
                      // data-chord on it, so tapping it opens the player rather than a piano with
                      // nothing to show.
                      // Silence, as wide as it lasts. Not a strike, so it takes no strike number.
                      if (rest) {
                        return (
                          // eslint-disable-next-line react/no-array-index-key
                          <span className="chord chord--rest" key={index} style={{ flexGrow: held ?? undefined }}>
                            <RestMark
                              countIn={countInEntry && line === 0 && bar === 0 && index === 0 ? (held ?? 0) : 0}
                            />
                          </span>
                        );
                      }

                      if (chord === null) {
                        return (
                          // eslint-disable-next-line react/no-array-index-key
                          <span className="chord chord--tie" key={index} style={{ flexGrow: held ?? undefined }}>
                            <span className="hold-mark" aria-hidden="true" />
                          </span>
                        );
                      }

                      // The inversion mark is drawn as a raised ordinal rather than as the '^1' it
                      // is stored as — see INVERSION_ORDINALS. data-chord keeps the symbol whole,
                      // since that is what the tap handler and the piano read.
                      const spelled = spellChord(chord);
                      const { symbol, inversion } = splitInversion(spelled);
                      const family = chordFamily?.(spelled);
                      const strike = strikes;
                      strikes += 1;
                      const isSounding = strike === currentChord && currentChordSeconds > 0;

                      return (
                        <span
                          className={`chord${isSlashChord(spelled) ? ' chord--slash' : ''}${marks > 0 ? ' chord--marked' : ''}${family ? ` chord-family--${family}` : ''}${isSounding ? ' chord--sounding' : ''}`}
                          data-chord={spelled}
                          // eslint-disable-next-line react/no-array-index-key
                          key={index}
                          // A chord's box is as wide as the beats it holds, and inside it those beats
                          // are equal columns — so a beat is the same width the whole way along the
                          // bar, whichever chord it belongs to, and "B / / C" reads as the four even
                          // beats it is rather than as symbols pushed to the edges of their boxes.
                          style={{
                            flexGrow: held ?? undefined,
                            ...(marks > 0 ? { '--slots': slots } : null),
                            ...(isSounding ? { '--sounding-seconds': `${currentChordSeconds}s` } : null),
                          }}
                        >
                          <span className="chord-name">
                            {symbol}
                            {inversion > 0 && <sup>{INVERSION_ORDINALS[inversion]}</sup>}
                          </span>
                          {/* The beats the chord is held for, written the way every chart writes
                              them — one per beat after the symbol, and only in a bar that was written
                              unevenly, since an even one says the same thing by having nothing to
                              say. A bar written finer than its beats has none either: see marks. */}
                          {Array.from({ length: marks }, (_, mark) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <span className="beat-mark" aria-hidden="true" key={mark}>
                              /
                            </span>
                          ))}
                        </span>
                      );
                    })
                  )}
                </span>
              ))}
              {Array.from({ length: fillersFor(line) }, (_, filler) => (
                // eslint-disable-next-line react/no-array-index-key
                <span
                  className={`bar bar--filler${narrow ? ` bar--narrow${holdsTakeSpare ? '' : ' bar--spare'}` : ''}`}
                  aria-hidden="true"
                  key={`filler-${filler}`}
                />
              ))}
            </span>
          ))}
        </span>

        {repeats && (
          <>
            <span className="repeat-sign repeat-sign--close" aria-hidden="true" />
            <span className="repeat-count">{`×${repeats}`}</span>
          </>
        )}
      </span>
    </span>
  );
}

ChordBarsChart.propTypes = {
  lines: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.string)).isRequired,
  sections: PropTypes.arrayOf(PropTypes.number),
  repeatCount: PropTypes.number,
  beatsPerBar: PropTypes.number,
  countInEntry: PropTypes.bool,
  currentBar: PropTypes.number,
  currentChord: PropTypes.number,
  currentChordSeconds: PropTypes.number,
  chordFamily: PropTypes.func,
  spellChord: PropTypes.func,
  onOpen: PropTypes.func,
};

export default ChordBarsChart;
